import {constructUsing, createMap, forMember, mapFrom, type Mapper} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {AdjustAgentDebtInput} from '@igp/shared/contract/pam';
import {DebtAdjustmentType, type DebtTransferLog} from '@igp/shared/contract/pam';
import {formatTimestamp} from '@igp/shared/utils';

import {
    AdjustAgentDebtInputTypeName,
    AdjustDebtFormModelTypeName,
    DebtTransferLogDownloadFieldsTypeName,
    DebtTransferLogDownloadModelTypeName,
    DebtTransferLogQueryFieldsTypeName,
    DebtTransferLogSortKeysTypeName,
    DebtTransferLogTypeName,
    DebtTransferLogViewModelKeysTypeName,
    DebtTransferLogViewModelTypeName,
} from './constants';
import type {
    AdjustDebtFormModel,
    DebtTransferLogDownloadFields,
    DebtTransferLogDownloadModel,
    DebtTransferLogQueryFields,
    DebtTransferLogSortKeys,
    DebtTransferLogViewModel,
    DebtTransferLogViewModelKeys,
} from './types';

export class DebtTransferLogMapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<DebtTransferLog, DebtTransferLogViewModel>(
            mapper,
            DebtTransferLogTypeName,
            DebtTransferLogViewModelTypeName,
            forMember(
                vm => vm.agentId,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.masterAgentId,
                mapFrom(m => m.master_agent_uid)
            ),
            forMember(
                vm => vm.createdAt,
                mapFrom(m => m.created_at)
            ),
            forMember(
                vm => vm.logId,
                mapFrom(m => m.log_id)
            ),
            forMember(
                vm => vm.transferDate,
                mapFrom(m => m.transfer_date)
            ),
            forMember(
                vm => vm.transferredAmount,
                mapFrom(m => m.transferred_amount)
            ),
            forMember(
                vm => vm.agentUsername,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.masterAgentUsername,
                mapFrom(m => m.master_agent_username)
            )
        );
        createMap<DebtTransferLog, DebtTransferLogDownloadModel>(
            mapper,
            DebtTransferLogTypeName,
            DebtTransferLogDownloadModelTypeName,
            forMember(
                vm => vm.agentId,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.masterAgentId,
                mapFrom(m => m.master_agent_uid)
            ),
            forMember(
                vm => vm.createdAt,
                mapFrom(m => formatTimestamp(m.created_at, 'date', false))
            ),
            forMember(
                vm => vm.logId,
                mapFrom(m => m.log_id)
            ),
            forMember(
                vm => vm.transferDate,
                mapFrom(m => formatTimestamp(m.transfer_date, 'date', false))
            ),
            forMember(
                vm => vm.transferredAmount,
                mapFrom(m => m.transferred_amount?.toString())
            ),
            forMember(
                vm => vm.agentUsername,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.masterAgentUsername,
                mapFrom(m => m.master_agent_username)
            )
        );
        createMap<DebtTransferLogViewModelKeys, DebtTransferLogQueryFields>(
            mapper,
            DebtTransferLogViewModelKeysTypeName,
            DebtTransferLogQueryFieldsTypeName,
            constructUsing(m => {
                const mapping: Record<DebtTransferLogViewModelKeys, DebtTransferLogQueryFields> = {
                    logId: 'log_id',
                    transferDate: 'transfer_date.seconds',
                    agentId: 'agent_uid',
                    agentUsername: 'agent_username',
                    masterAgentId: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    transferredAmount: 'transferred_amount',
                    createdAt: 'created_at.seconds',
                };

                return mapping[m];
            })
        );
        createMap<DebtTransferLogViewModelKeys, DebtTransferLogDownloadFields>(
            mapper,
            DebtTransferLogViewModelKeysTypeName,
            DebtTransferLogDownloadFieldsTypeName,
            constructUsing(m => {
                const mapping: Record<DebtTransferLogViewModelKeys, DebtTransferLogDownloadFields> = {
                    logId: 'log_id',
                    transferDate: 'transfer_date.seconds',
                    agentId: 'agent_uid',
                    agentUsername: 'agent_username',
                    masterAgentId: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    transferredAmount: 'transferred_amount',
                    createdAt: 'created_at.seconds',
                };

                return mapping[m];
            })
        );

        createMap<DebtTransferLogViewModelKeys, DebtTransferLogSortKeys>(
            mapper,
            DebtTransferLogViewModelKeysTypeName,
            DebtTransferLogSortKeysTypeName,
            constructUsing(m => {
                const mapping: Record<DebtTransferLogViewModelKeys, DebtTransferLogSortKeys> = {
                    logId: 'log_id',
                    transferDate: 'transfer_date.seconds',
                    agentId: 'agent_uid',
                    agentUsername: 'agent_username',
                    masterAgentId: 'master_agent_uid',
                    masterAgentUsername: 'master_agent_username',
                    transferredAmount: 'transferred_amount',
                    createdAt: 'created_at',
                };

                return mapping[m];
            })
        );

        createMap<DebtTransferLogSortKeys, DebtTransferLogViewModelKeys>(
            mapper,
            DebtTransferLogSortKeysTypeName,
            DebtTransferLogViewModelKeysTypeName,
            constructUsing(m => {
                const mapping: Record<DebtTransferLogSortKeys, DebtTransferLogViewModelKeys> = {
                    log_id: 'logId',
                    'transfer_date.seconds': 'transferDate',
                    agent_uid: 'agentId',
                    master_agent_uid: 'masterAgentId',
                    transferred_amount: 'transferredAmount',
                    created_at: 'createdAt',
                    agent_username: 'agentUsername',
                    master_agent_username: 'masterAgentUsername',
                };

                return mapping[m];
            })
        );
        createMap<AdjustDebtFormModel, AdjustAgentDebtInput>(
            mapper,
            AdjustDebtFormModelTypeName,
            AdjustAgentDebtInputTypeName,
            forMember(
                input => input.adjust_type,
                mapFrom(form => (form.adjustType === 'Add' ? DebtAdjustmentType.Add : DebtAdjustmentType.Deduct))
            ),
            forMember(
                input => input.agent_uid,
                mapFrom(form => form.agentUid)
            ),
            forMember(
                input => input.amount,
                mapFrom(form => Number(form.amount))
            ),
            forMember(
                input => input.transfer_date,
                mapFrom(form => form.transferDate)
            )
        );
    }
}
